import React, { useMemo } from "react";
import { useTable } from "react-table";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";

import moment from "moment";
import { ReactComponent as PackageIcon } from "assets/images/iconsV2/package.svg";
import { ReactComponent as WeightIcon } from "assets/images/iconsV2/weight.svg";
import { ReactComponent as VolumeIcon } from "assets/images/iconsV2/volume-cubic-meter.svg";
import { ReactComponent as PlaneIcon } from "assets/images/iconsV2/plane-grey.svg";
import { ReactComponent as CargoShipIcon } from "assets/images/iconsV2/cargo-ship.svg";

import DataTable from "components/v2/Table/DataTable";
import { formatScheduleTime } from "helpers/multistopHelpers";
import { calculateGoodItemsTotalWeight } from "helpers/quotationFormHelper";
import { WeightUnitTypes } from "constants/";
import { IGoodItem, IGoodItemStopDetails } from "pages/provider/QuotationDetail/QuotationType";
import "./MultiStopTable.scss";

function findStopIndex(scheduleStops: any[], stop: IGoodItemStopDetails): number {
  return scheduleStops.findIndex(
    (s) =>
      s.stop_type === stop.stop_type &&
      s.stop_after_datetime === stop.stop_after_datetime &&
      s.stop_before_datetime === stop.stop_before_datetime,
  );
}

function formatQuotedDateTime(quotedDate?: string, quotedTime?: string): string {
  const intl = useIntl();
  if (!quotedDate || !quotedTime) return intl.formatMessage({ id: "general.notSetYet", defaultMessage: "Not set yet" });
  try {
    const date = moment(quotedDate).format("DD MMM YYYY");
    const time = quotedTime.slice(0, 5); // "HH:mm" from "HH:mm:ss"
    return `${date}, ${time}`;
  } catch {
    return intl.formatMessage({ id: "general.notSetYet", defaultMessage: "Not set yet" });
  }
}

function StopAddressCell({
  stop,
  quotedDate,
  quotedTime,
}: {
  stop?: IGoodItemStopDetails | null;
  quotedDate?: string;
  quotedTime?: string;
}) {
  const intl = useIntl();

  if (!stop) return <span>-</span>;

  const { stop_after_datetime, stop_before_datetime, stop_time_type, stop_timezone } = stop;

  const requestedText = formatScheduleTime({
    stop_after_datetime,
    stop_before_datetime,
    stop_time_type,
    stop_timezone,
    intl,
  });

  const isNotSet = !quotedDate || !quotedTime;
  const quotedText = formatQuotedDateTime(quotedDate, quotedTime);

  return (
    <div className="multi-stop-cell-stop">
      <div className="multi-stop-cell-stop__dates">
        <div className={`multi-stop-cell-stop__date${isNotSet ? " multi-stop-cell-stop__date--not-set" : ""}`}>
          {quotedText || requestedText}
        </div>
        <div className="multi-stop-cell-stop__requested">
          <FormattedMessage id="general.requested" defaultMessage="Requested" />: {requestedText}
        </div>
      </div>
      <div className="multi-stop-cell-stop__address">
        {stop.stop_address_type === "Airport" && stop.airport_details && (
          <div className="multi-stop-cell-stop__facility">
            <span className="multi-stop-cell-stop__badge">
              <PlaneIcon />
              <FormattedMessage id="general.airport" defaultMessage="Airport" />
            </span>
            <span className="multi-stop-cell-stop__facility-name">
              {stop.airport_details.name} ({stop.airport_details.code})
            </span>
          </div>
        )}
        {stop.stop_address_type === "Port" && stop.seaport_details && (
          <div className="multi-stop-cell-stop__facility">
            <span className="multi-stop-cell-stop__badge">
              <CargoShipIcon />
              <FormattedMessage id="general.port" defaultMessage="Port" />
            </span>
            <span className="multi-stop-cell-stop__facility-name">
              {stop.seaport_details.name} ({stop.seaport_details.un_locode})
            </span>
          </div>
        )}
        <div>
          {stop.address_details.street_address}
          {stop.address_details.street_address_line_two && <>, {stop.address_details.street_address_line_two}</>}
        </div>
        <div>
          {stop.address_details.state && <>{stop.address_details.state}, </>}
          {stop.address_details.city}
          {stop.address_details.zipcode && <>, {stop.address_details.zipcode}</>}
          {stop.address_details.country && <> {stop.address_details.country}</>}
        </div>
      </div>
    </div>
  );
}

export default function MultiStopTable({
  good_items,
  schedule_stops = [],
  shipper,
}: {
  good_items: IGoodItem[];
  schedule_stops?: any[];
  shipper?: string;
}) {
  const intl = useIntl();
  const { watch } = useFormContext();
  const formScheduleStops = watch("schedule.stops");

  const { totalPackages, totalWeight, totalCbm } = useMemo(() => {
    let packages = 0;
    let cbm = 0;

    good_items.forEach((item) => {
      packages += item.units || 0;
      cbm += item.total_cbm || 0;
    });

    const { weight } = calculateGoodItemsTotalWeight(good_items, WeightUnitTypes.KG);

    return { totalPackages: packages, totalWeight: weight, totalCbm: cbm };
  }, [good_items]);

  const columns: any = useMemo(
    () => [
      {
        Header: "Qty",
        accessor: "units",
        width: 40,
      },
      {
        Header: intl.formatMessage({ id: "new-request.packing-list.unit", defaultMessage: "Unit" }),
        accessor: "handling_unit",
        width: 140,
        Cell: ({ row }: { row: { original: IGoodItem } }) => {
          const item = row.original;
          const hasDimensions = item.length && item.width && item.height && item.m_unit;
          return (
            <div className="multi-stop-cell-unit">
              <div className="multi-stop-cell-unit__label" style={{ textTransform: "uppercase" }}>
                {item.handling_unit}
              </div>
              {hasDimensions && (
                <div className="multi-stop-cell-unit__dimensions">
                  {item.length} x {item.width} x {item.height}
                  {item.m_unit}
                </div>
              )}
              {item.container_type && <div className="multi-stop-cell-unit__dimensions">{item.container_type}</div>}
              <div className="multi-stop-cell-unit__weight">
                {item.weight}
                {item.w_unit}
                <span className="multi-stop-cell-unit__weight-suffix">/unit</span>
              </div>
            </div>
          );
        },
      },
      {
        Header: intl.formatMessage({ id: "general.handling", defaultMessage: "Handling" }),
        accessor: "stackable",
        width: 240,
        Cell: ({ row }: { row: { original: IGoodItem } }) => {
          const item = row.original;
          return (
            <div className="multi-stop-cell-handling">
              <div>
                <FormattedMessage id="quote-request-detail.stackable" defaultMessage="Stackable" />:{" "}
                <span className="multi-stop-cell-handling__value">
                  {item.stackable
                    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                    : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                </span>
              </div>
              {item.hazardous && (
                <div>
                  <FormattedMessage id="quotation-form.viewCommodities.hazardous" defaultMessage="Hazardous" />:{" "}
                  <span className="multi-stop-cell-handling__value">
                    {intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })}
                  </span>
                </div>
              )}
              {item.crating_required && (
                <div>
                  <FormattedMessage id="general.cratingBy" defaultMessage="Crating by" />:{" "}
                  <span className="multi-stop-cell-handling__value">
                    {item.crating_required_by_date ? moment(item.crating_required_by_date).format("DD MMM YYYY") : "-"}
                  </span>
                </div>
              )}
              {item.is_dangerous_good && (
                <div>
                  DG: <span className="multi-stop-cell-handling__value">{item.dangerous_good_value || "-"}</span>
                </div>
              )}
              {item.temperature_control_required && (
                <div>
                  Temp: <span className="multi-stop-cell-handling__value">{item.temperature_control_value || "-"}</span>
                </div>
              )}
              {item.handling_required && (
                <div>
                  <FormattedMessage id="general.handling" defaultMessage="Handling" />:{" "}
                  <span className="multi-stop-cell-handling__value">
                    {intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" }),
        accessor: "pickup_stop",
        width: 320,
        Cell: ({ row }: { row: { original: IGoodItem } }) => {
          const stop = row.original.pickup_stop;
          const idx = stop ? findStopIndex(schedule_stops, stop) : -1;
          const quotedStop = idx >= 0 ? formScheduleStops?.[idx] : undefined;
          return <StopAddressCell stop={stop} quotedDate={quotedStop?.quoted_date} quotedTime={quotedStop?.quoted_time} />;
        },
      },
      {
        Header: intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" }),
        accessor: "delivery_stop",
        width: 320,
        Cell: ({ row }: { row: { original: IGoodItem } }) => {
          const stop = row.original.delivery_stop;
          const idx = stop ? findStopIndex(schedule_stops, stop) : -1;
          const quotedStop = idx >= 0 ? formScheduleStops?.[idx] : undefined;
          return <StopAddressCell stop={stop} quotedDate={quotedStop?.quoted_date} quotedTime={quotedStop?.quoted_time} />;
        },
      },
      {
        Header: intl.formatMessage({ id: "general.instructions", defaultMessage: "Instructions" }),
        accessor: "additional_information",
        Cell: ({ value }: { value: string }) => <div className="multi-stop-cell-instructions">{value || "-"}</div>,
      },
    ],
    [intl, schedule_stops, formScheduleStops],
  );

  const tableInstance = useTable({
    columns,
    data: good_items,
  });

  return (
    <div className="mb-3xl multi-stop-packing-list">
      <h6 className="mb-lg">
        <FormattedMessage id="general.schedule" defaultMessage="Schedule" />
      </h6>
      <div className="multi-stop-summary">
        <div className="multi-stop-counter">
          <span className="multi-stop-counter__label">
            <PackageIcon />
            <FormattedMessage id="quote-request-detail.totalPackages" defaultMessage="Total Packages" />
          </span>
          <span className="multi-stop-counter__value">{totalPackages}</span>
        </div>
        <div className="multi-stop-counter">
          <span className="multi-stop-counter__label">
            <WeightIcon />
            <FormattedMessage id="general.totalWeight" defaultMessage="Total Weight" />
          </span>
          <span className="multi-stop-counter__value">{totalWeight.toFixed(2)} kg</span>
        </div>
        <div className="multi-stop-counter">
          <span className="multi-stop-counter__label">
            <VolumeIcon />
            <FormattedMessage id="quote.totalCBM" defaultMessage="Total CBM" />
          </span>
          <span className="multi-stop-counter__value">
            {totalCbm.toFixed(3)} m<sup>3</sup>
          </span>
        </div>
        {/* <div className="multi-stop-counter">
          <span className="multi-stop-counter__label">
            <CurrencyIcon />
            <FormattedMessage id="quote-request-detail.totalCustomsValue" defaultMessage="Total customs value" />
          </span>
          <span className="multi-stop-counter__value">{totalCustomsValue.toFixed(2)}</span>
        </div> */}
      </div>
      <DataTable
        tableInstance={tableInstance}
        customNoResults={
          <span>
            <FormattedMessage id="general.noPackingListEnteredBy" defaultMessage="No packing list entered by" /> {shipper}
          </span>
        }
      />
    </div>
  );
}
